import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Timer, Check, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DealCountdown } from "@/components/DealCountdown";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

export const DealsTab = () => {
  const [opps, setOpps] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/opportunities").then((r) => setOpps(r.data)).catch(() => {}); }, []);

  const save = async (o, clear = false) => {
    setBusy(o.slug);
    try {
      const value = clear ? null : drafts[o.slug];
      const res = await api.patch(`/admin/opportunities/${o.slug}/deal`, { closes_at: value ? new Date(value).toISOString() : null });
      setOpps((prev) => prev.map((p) => (p.slug === o.slug ? { ...p, closes_at: res.data.closes_at } : p)));
      setDrafts((d) => { const n = { ...d }; delete n[o.slug]; return n; });
      toast.success(clear ? `${o.name}: deal window cleared` : `${o.name}: closes ${new Date(res.data.closes_at).toLocaleString("en-IN")}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(null); }
  };

  const active = opps.filter((o) => o.closes_at);
  const shown = opps.filter((o) => o.name.toLowerCase().includes(q.toLowerCase())).sort((a, b) => (b.closes_at ? 1 : 0) - (a.closes_at ? 1 : 0));

  return (
    <div data-testid="admin-deals-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Deal Windows</h2>
          <p className="mt-1 text-sm text-slate-500">Set a closing time on limited-liquidity shares — investors see a live "closing soon" countdown and buying stops when it lapses.</p>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="admin-deals-active-count" className="flex items-center gap-1.5 border border-[#6D28D9]/40 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#6D28D9]"><Timer className="h-3.5 w-3.5" /> {active.length} live</span>
          <input data-testid="admin-deals-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search company…" className="w-56 border border-slate-200 bg-[#FFFFFF]/60 px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none focus:border-[#6D28D9]/60" />
        </div>
      </div>
      <div className="mt-8 overflow-x-auto border border-slate-200">
        <table className="w-full min-w-[760px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-[#F6F4FD] font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
              <th className="px-5 py-4">Company</th><th className="px-5 py-4">Countdown</th><th className="px-5 py-4">Closes at (local time)</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((o) => (
              <tr key={o.slug} data-testid={`admin-deal-row-${o.slug}`} className="border-b border-slate-100 hover:bg-violet-50">
                <td className="px-5 py-3"><p className="font-medium text-slate-800">{o.name}</p><p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">{o.status}</p></td>
                <td className="px-5 py-3">{o.closes_at ? <DealCountdown closesAt={o.closes_at} testid={`admin-deal-countdown-${o.slug}`} /> : <span className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-400">No window</span>}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <input data-testid={`admin-deal-input-${o.slug}`} type="datetime-local" value={drafts[o.slug] ?? toLocalInput(o.closes_at)} onChange={(e) => setDrafts((d) => ({ ...d, [o.slug]: e.target.value }))} className="border border-slate-200 bg-[#FFFFFF]/60 px-3 py-2 font-mono2 text-xs text-slate-800 outline-none focus:border-[#6D28D9]/60" />
                    <button data-testid={`admin-deal-save-${o.slug}`} onClick={() => save(o)} disabled={!drafts[o.slug] || busy === o.slug} className="flex items-center gap-1 bg-[#6D28D9] px-3 py-2 font-mono2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#FFFFFF] hover:bg-[#5B21B6] disabled:opacity-30"><Check className="h-3 w-3" /> Set</button>
                    {o.closes_at && <button data-testid={`admin-deal-clear-${o.slug}`} onClick={() => save(o, true)} disabled={busy === o.slug} className="flex items-center gap-1 border border-slate-200 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500 hover:text-rose-700 disabled:opacity-30"><X className="h-3 w-3" /> Clear</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
